import React, { useState } from 'react';
import styled from 'styled-components';
import VideoPlayer from './VideoPlayer';
import StatsPanel from './StatsPanel';
import StreamInfo from './StreamInfo';
import EmbedCode from './EmbedCode';

const DashboardContainer = styled.div`
  background: #f5f7fa;
  min-height: 100vh;
`;

const MainContent = styled.main`
  padding: 24px;
  background: #f5f7fa;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PageHeader = styled.header`
  background: white;
  padding: 24px 32px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-left: 4px solid #667eea;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    text-align: center;
    padding: 20px;
  }
`;

const HeaderContent = styled.div``;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 4px;
`;

const PageSubtitle = styled.p`
  color: #718096;
  font-size: 0.875rem;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &.primary {
    background: #667eea;
    color: white;
    
    &:hover {
      background: #5a67d8;
      transform: translateY(-1px);
    }
  }
  
  &.secondary {
    background: white;
    color: #4a5568;
    border: 1px solid #e2e8f0;
    
    &:hover {
      border-color: #667eea;
      color: #667eea;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
`;

const MainSection = styled.section`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 20px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
  }
  
  i {
    color: #667eea;
    font-size: 1.125rem;
  }
`;

const SectionContent = styled.div`
  padding: 24px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`;

const Dashboard = () => {
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <DashboardContainer>
      <MainContent>
        <PageHeader>
          <HeaderContent>
            <PageTitle>Dashboard</PageTitle>
            <PageSubtitle>Monitor your streaming operations</PageSubtitle>
          </HeaderContent>
          
          <HeaderActions>
            <ActionButton className="secondary" onClick={handleRefresh}>
              <i className="fas fa-sync-alt"></i>
              Refresh
            </ActionButton>
            <ActionButton className="primary">
              <i className="fas fa-cog"></i>
              Settings
            </ActionButton>
          </HeaderActions>
        </PageHeader>

        <ContentGrid>
          {/* Statistics Section */}
          <MainSection>
            <SectionHeader>
              <i className="fas fa-chart-bar"></i>
              <h2>System Status</h2>
            </SectionHeader>
            <SectionContent>
              <StatsPanel key={refreshKey} />
            </SectionContent>
          </MainSection>

          {/* Video Player Section */}
          <MainSection>
            <SectionHeader>
              <i className="fas fa-video"></i>
              <h2>Live Stream</h2>
            </SectionHeader>
            <SectionContent>
              <VideoPlayer key={refreshKey} />
            </SectionContent>
          </MainSection>

          {/* Information Panels */}
          <InfoGrid>
            <MainSection>
              <SectionHeader>
                <i className="fas fa-info-circle"></i>
                <h2>Stream Information</h2>
              </SectionHeader>
              <SectionContent>
                <StreamInfo key={refreshKey} />
              </SectionContent>
            </MainSection>

            <MainSection>
              <SectionHeader>
                <i className="fas fa-code"></i>
                <h2>Embed Code</h2>
              </SectionHeader>
              <SectionContent>
                <EmbedCode key={refreshKey} />
              </SectionContent>
            </MainSection>
          </InfoGrid>
        </ContentGrid>
      </MainContent>
    </DashboardContainer>
  );
};

export default Dashboard; 